import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { i18n } from '../../../../translate/i18n';
import {
  Paper,
  Typography,
  FormControl,
  IconButton,
  Container,
  TextField,
  Divider,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import * as actions from "../../../../store/actions";
import Tabs from '../../../../components/Tabs/TabsEquipment';
import {
  FormStyled,
  DoneIconStyled,
  DivSubmitButtons,
  DivFormTitle,
} from './../styles';
import Spinner from '../../../../components/Spinner/index';
import FormLabelWithContent from '../../../../UI/FormLabelWithContent';
import moment from 'moment';
import numberToCurrency from '../../../../utils/numberToCurrency';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { EAccessRole } from '../../../../store/types';

const FormEquipmentsFinance = (props) => {
  const equipId = props.match.params.id
  const backPage = props.match.params.back_page;
  const backPageId = props.match.params.back_page_id;
  const placeId = equipId === 'new' && backPage === 'admin-equip-places' && backPageId ? backPageId : null;

  const dispatch = useDispatch();
  const matches = useMediaQuery('(min-width:1200px)');

  const [editMode, setEditMode] = useState(false);
  const [isNewData, setIsNewData] = useState(true);
  const [serialNumber, setSerialNumber] = useState('')

  // forms equipments
  const [amount, setAmount] = useState('')
  const [acquisitionDate, setAcquisitionDate] = useState(null)
  const [selectedPlaces, setSelectedPlaces] = React.useState([]);
  // fetch from BE

  const fetchEquipment = useCallback(
    () => dispatch(actions.fetchEquipmentsById(equipId))
  );

  // get from redux
  const equipmentModel = useSelector(state => state.equipments.equipmentModel)
  const equipment = useSelector(state => state.equipments.equipment);
  const selectedAccess = useSelector(state => state.auth.selectedAccess);
  const isAnalyst = selectedAccess.role === EAccessRole.analyst;
  const isContractor = selectedAccess.role === EAccessRole.contractor;
  const isInspector = selectedAccess.inspector && (isAnalyst || isContractor);

  const fetchPlace = useCallback(
    () => dispatch(actions.equipmentsFetchPlacesById(placeId))
  );
  
  const place = useSelector(state => state.equipments.place);
  const loadingEquipments = useSelector(state => state.equipments.loading);
  
  useEffect(() => {
    const placeFormated = {
      value: place._id,
      label: `${place.name} - (${place.path})`
    }
    setSelectedPlaces(placeFormated)
  }, [place])
  // check if is newEntrance or editing
  useEffect(() => {
    setIsNewData(equipId && equipId === 'new')

    if (equipId && equipId !== 'new') {
      fetchEquipment()
    } else if (placeId) {
      dispatch(actions.clearEquipmentsEquipmentModelsById())
    } else {
      setSelectedPlaces({})
      dispatch(actions.clearEquipmentsEquipmentModelsById())
    }
  }, [])

  // in case editing, populate fields
  useEffect(() => {
    if (!isEmpty(equipment) && equipId && equipId !== 'new') {
      setAmount(equipment.amount > 0 ? numberToCurrency(equipment.amount) : '');
      equipment.acquisitionDate ? setAcquisitionDate(equipment.acquisitionDate) : setAcquisitionDate(null)
      setSerialNumber(equipment.serialNumber ? equipment.serialNumber : '')
      let place = {}
      place = { value: equipment.place._id, label: `${equipment.place.name} - (${equipment.place.path})` }
      setSelectedPlaces(place)
    }
  }, [equipment, equipId])

  // send data to Server (post and update)
  const updateEquipmentFinance = (body, message = '') => dispatch(actions.updateEquipmentFinance(equipId, body, message))

  const currencyToNumber = text => {
    return Number(
      text
        .toString()
        .replace('R$ ', '')
        .replace(/\./g, '')
        .replace(',', '.'),
    );
  };

  const onlyNumbers = value => {
    return value.replace(/\D/g, '');
  };

  const handleCurrency = value => {
    const string = onlyNumbers(value)
    setAmount(string);
  }

  function currencyFormat(num) {
    return num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }

  const calculateResidualValue = () => {
    if(!equipment.acquisitionDate || !equipment.equipmentModel?.annualDepreciation || !amount) {
      return '-'
    } else {
      const depreciation = equipment.equipmentModel.annualDepreciation / 100;
      const period = moment(moment(new Date())).diff(equipment.acquisitionDate, 'years', true);
      const value = equipment.amount / 100;
  
      const result = (1 - depreciation * period ) * Number(value);
      return `$ ${currencyFormat(result)}`;
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;

    if (isEmpty(selectedPlaces)) {
      hasError = true
    }

    // To check is has a invalid deadline
    if (isNewData) {
      if (equipmentModel && equipmentModel.equipmentServices && equipmentModel.equipmentServices.length > 0) {
        equipmentModel.equipmentServices.forEach(service => {
          if (service.expiration && !service.isValid) {
            hasError = true;
          }
        })
      }
    }

    if (hasError) return

    const body = {
      "place": selectedPlaces.value,
      "serialNumber": serialNumber,
      "amount": currencyToNumber(amount),
      "acquisitionDate": acquisitionDate,
    }

    if (isNewData || placeId) {
      // payload para servicos ja feitos
      if (equipmentModel && equipmentModel.equipmentServices.length > 0) {
        const equipServices = []
        equipmentModel.equipmentServices.forEach(service => {
          if (service.expiration) {
            equipServices.push({
              equipmentServiceId: service._id,
              expiration: service.expiration,
            })
          }
        })
        body.services = equipServices
      }
    } else {
      updateEquipmentFinance(body).then(result => {
        if (result === 'success') {
          dispatch(actions.setDataChangedForceReload(true))
          fetchEquipment()
          setEditMode(false)
        }
      })
    }
  }

  const handleClose = () => {
    const profileArray = []
    profileArray.push(equipment.photo);
    setSerialNumber(equipment.serialNumber ? equipment.serialNumber : '')
    setAmount(equipment.amount > 0 ? numberToCurrency(equipment.amount) : '');
    equipment.acquisitionDate ? setAcquisitionDate(equipment.acquisitionDate) : setAcquisitionDate(null)

    setEditMode(false);
  }

  const handleAcquisitionDateChange = date => {
    setAcquisitionDate(date);
  }

  return <>
          <Tabs {...props} selected={'equipmentFinanceTab'} />
    <div style={matches ? styles.divContainer : styles.divContainerSmall}>
      <Container maxWidth='md' style={styles.container}>
        {loadingEquipments && <Spinner overlay />}
        <Paper>
          <FormStyled onSubmit={handleSubmit} style={{ opacity: loadingEquipments ? '0.3' : '1' }}>
            <DivFormTitle>
              <Typography variant='h6'>{equipId === 'new' || placeId ? i18n.t('divFormTitle.typographyTrue') : i18n.t('divFormTitle.typographyFinancialInfo')}</Typography>
              {!isNewData && !placeId &&
                <DivSubmitButtons>
                  {editMode ?
                    <>
                      <IconButton onClick={handleClose} size="large"><CloseIcon color='error' /></IconButton>
                      <IconButton type="submit" size="large"><DoneIconStyled /></IconButton>
                    </> :
                    <>
                      <IconButton disabled={isInspector} onClick={() => setEditMode(true)} size="large"><EditIcon /></IconButton>
                    </>}
                </DivSubmitButtons>}
            </DivFormTitle>
            <Divider />
            <>
              <FormControl variant="standard" fullWidth>

                {
                  (editMode || isNewData) ?
                  <TextField
                    variant="standard"
                    disabled={!isNewData && !editMode && !placeId}
                    label={i18n.t('textFieldFormEquip.amount')}
                    name='Amount'
                    onChange={event => handleCurrency(event.target.value)}
                    value={numberToCurrency(amount)}
                    InputProps={{
                      readOnly: !isNewData && !editMode,
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    // error={amount === 0 || (!amount && editMode)}
                    helperText={(!amount || amount <= 0) && 'Por Favor adicione um Valor'}
                    margin='normal' />
                  : <FormLabelWithContent label={i18n.t('textFieldFormEquip.amount')} value={onlyNumbers(amount) > 0 ? '$ ' + numberToCurrency(amount) : '-'} />

                }
              </FormControl>

              <div>
              <FormControl variant="standard" maxWidth>
                {(editMode || isNewData) ?
                    <DatePicker
                    label={i18n.t('textFieldFormEquip.acquisitionDate')}
                      margin='normal'
                      name='DateOfAcquisition'
                      onChange={date => { handleAcquisitionDateChange(date) }}
                      value={acquisitionDate && new Date(acquisitionDate)}
                      // maxDate={new Date()}
                      format="dd/MM/yyyy"
                      disabled={!isNewData && !editMode && !placeId}
                      // error={!acquisitionDate && editMode}
                      helperText={!acquisitionDate && 'Por Favor adicione uma Data'}
                      slotProps={{ textField: { variant: 'standard', } }}
                    />
                    : <FormLabelWithContent label={i18n.t('textFieldFormEquip.acquisitionDate')} value={acquisitionDate === null ? '-' : moment(acquisitionDate).format('DD/MM/YYYY')} />}
              </FormControl>
              </div>
            </>
            {(isNewData || placeId) &&
              <DivSubmitButtons>
                <IconButton onClick={props.history.goBack} size="large"><CloseIcon fontSize='large' color='error' /></IconButton>
                <IconButton type="submit" size="large"><DoneIconStyled fontSize='large' /></IconButton>
              </DivSubmitButtons>}
          </FormStyled>
        </Paper>
      </Container>

      <Container maxWidth='md' style={styles.container}>
      <Paper>
        <FormStyled style={{ opacity: loadingEquipments ? '0.3' : '1' }}>
          <FormLabelWithContent label={i18n.t('textFieldFormEquip.residualValue')} value={calculateResidualValue()} />
        </FormStyled>
        </Paper>
      </Container>
    </div>
  </>;
}

export default FormEquipmentsFinance;

const styles = {
  arrowBack: {
    position: 'fixed',
    marginTop: 10,
  },
  divContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 50,
    flexDirection: 'column'
  },
  divContainerSmall: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    padding: 10,
    margin: 0,
    marginLeft: 50,
    position: 'relative',
    display: 'flex', flexDirection: 'column', justifyContent: 'center'
  },
  avatarContainer: {
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    width: 195,
    height: 195,
    marginTop: 20,
  },
  avatarButton: {
    margin: 5,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  restoreButton: {
    backgroundColor: 'red',
    color: 'white',
    padding: '8px',
    margin: '7px 0px',
  },
  photoQRContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  photoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  iconQrCode: {
    width: 30,
  },
}
